import { Injectable } from '@angular/core';
import { RootScopeService } from './root-scope.service';
import { GenesysCloudService } from './genesys-cloud.service';
import { Global } from '../global/globalApi';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { AuthMethodService } from './auth-method.service';
import { NewAgentApiService } from './new-agent-api.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { interval } from 'rxjs/internal/observable/interval';
import { CallCustomerAttributes } from '../models/customer.model';
import * as Constants from 'src/app/models/Constants';
import { ApiService } from './api.service';
import { Tab } from '../models/tab.model';

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
 public intervalId: any = null;
  public startTime: number = 0;
  private timerSubscription?: Subscription;
  activeTab: Tab = new Tab();
  fileName : string = 'biometrics.service.ts';
  isbuttonDisabled: boolean = false;

  constructor(public rootScope: RootScopeService, public apiService: ApiService, public agentService: NewAgentApiService, public authService: AuthMethodService, public http: HttpClient, public genesysCloudService: GenesysCloudService) {
    this.activeTab = this.rootScope.activeCustTab;
  }

  /**
    * CCR3-15498 -monitorBiometricStatus() - polling the biometric status API at regular intervals to check the status of the biometric authentication process.
    * It starts a timer and fetches the biometric status every 5 seconds until either the maximum duration of 2.5 minutes is reached or the biometric authentication is completed.
    * If the maximum duration is reached, it stops polling and clears the interval.
    * @author Amarjeet
    */
   public monitorBiometricStatus() {
    const intervalTime = this.rootScope.getObjFromConfigObj(Constants.biometricConfig.API_INTERVAL_TIME);
    const pollingTime = this.rootScope.getObjFromConfigObj(Constants.biometricConfig.API_POLLING_TIME);
    const pollEvery2sec = intervalTime ? parseInt(intervalTime?.['fieldValue']) * 1000 : this.rootScope.CCTConfigs.BIOMETRIC_POLL_API_INTERVAL; // 5 seconds
    const maxDuration2_5Mns = pollingTime ? parseInt(pollingTime?.['fieldValue']) * 1000 : this.rootScope.CCTConfigs.MAX_BIOMETRIC_POLL_DURATION; // 160 secondss
    const startedAt = Date.now();
    this.startTimer(); 
    this.fetchBiometricStatus();

    this.intervalId = setInterval(() => {
      if (Date.now() - startedAt >= maxDuration2_5Mns) {        
        this.stopTimer();
        return;
      }
      this.fetchBiometricStatus();
    }, pollEvery2sec);
  }

  
  /**
    * CCR3-15498 - startTimer() - starts a timer to track the duration of the biometric authentication process.
    * It subscribes to an interval observable that ticks every 5 seconds.
    * @author Amarjeet
    */
  startTimer(): void {
    this.timerSubscription?.unsubscribe();    
    this.timerSubscription = interval(this.rootScope.CCTConfigs.BIOMETRIC_POLL_API_INTERVAL).subscribe(() => {
      //targetData.timer = Date.now() - this.startTime;
    });
  }

    /**
    * stopTimer() - stops the timer tracking the duration of the biometric authentication process.
    * It unsubscribes from the interval observable.
    * @author Amarjeet
    */
  stopTimer() {   
    clearInterval(this.intervalId);
    this.timerSubscription?.unsubscribe();
    //targetData.timer = 0; 
  }

  /**
   * CCR3-15483: Initiates biometric workflow and updates UI step state based on API response.
   * This method is triggered when the user clicks the "Request Biometrics" button.
   * It calls the initiate biometric endpoint and handles the response to update the UI accordingly.
   */
  sendBiometric(){
    // Call initiate biometric endpoint for customer account.
    this.isbuttonDisabled = true;
    this.apiService.getMethodForCustomerAcc(Global.biometric.INITIATE_BIOMETRIC).subscribe({
      next: (response: any) => {
        // On successful initiation, mark step 1 complete and move to step 2.
         /* istanbul ignore next */
        if (response?.httpStatusCode === HttpStatusCode.Ok) {
          this.activeTab.biometricData.step1Complete = true;
          this.activeTab.biometricData.step1Active = false;
          this.activeTab.biometricData.step2Active = true;
          this.rootScope.triggerLog("sendBiometric response: " + JSON.stringify(response), this.fileName, Constants.DEBUG);
          this.monitorBiometricStatus();          
          
        }
          this.isbuttonDisabled = false;
      },
      error: (error: any) => {
        // On failure, flag step 1 as failed for retry/handling in UI.
        this.isbuttonDisabled = false;
        this.activeTab.biometricData.step1Failed = true;
        this.rootScope.triggerLog("sendBiometric error: " + JSON.stringify(error), this.fileName);
      }
    });
  }

   /**
    * CCR3-15498 -fetchBiometricStatus() - polls the biometric status API at regular intervals to check the status of the biometric authentication process.
    * It starts a timer and fetches the biometric status every 5 seconds until either the maximum duration of 2.5 minutes is reached or the biometric authentication is completed.
    * If the maximum duration is reached, it stops polling and clears the interval.
    * @author Amarjeet
    */
  fetchBiometricStatus() {
    // Call fetch biometric status endpoint for customer account.
    this.apiService.getMethodForCustomerAcc(Global.biometric.FETCH_BIOMETRIC_STATUS).subscribe({
      next: (response: any) => {       
        // On successful fetch, update the biometric status in the UI.       
        if (response?.httpStatusCode === HttpStatusCode.Ok) {    
          if (response?.data?.biometricAuthStatus === Constants.biometricConfig.APPROVED) {
            this.activeTab.biometricData.step2Complete = true;
            this.activeTab.biometricData.biometricCompleted = true;         
            this.stopTimer();
            this.updateSecurityForPpt();
            this.rootScope.triggerLog(`PollCallbackResponse: ${JSON.stringify(response)}`, this.fileName, Constants.DEBUG);
          }
          else if (response?.data?.biometricAuthStatus === Constants.biometricConfig.EXPIRED) {
            ++this.activeTab.biometricData.expiredAttempts;
            this.activeTab.biometricData.step2Complete = false;
            this.activeTab.biometricData.step2Failed = true;
            this.activeTab.biometricData.biometricCompleted = false;
            if(this.activeTab.biometricData.expiredAttempts < Constants.biometricConfig.MAX_EXPIRED_ATTEMPTS) {
              this.activeTab.biometricData.step2ErrorMsg = Constants.MB_MESSAGES.BIOMETRIC_REQUEST_EXPIRED;
            }
            else{
              this.activeTab.biometricData.step2ErrorMsg = Constants.MB_MESSAGES.BIOMETRIC_MAX_ATTEMPTS_REACHED;
            }
            this.stopTimer();
            this.rootScope.triggerLog("fetchBiometricStatus error: " + JSON.stringify(response), this.fileName);
          }
          else if (response?.data?.biometricAuthStatus === Constants.biometricConfig.DENIED) {
            this.activeTab.biometricData.step2Complete = false;
            this.activeTab.biometricData.step2Failed = true;
            this.activeTab.biometricData.biometricCompleted = false;
            this.activeTab.biometricData.step2ErrorMsg = Constants.MB_MESSAGES.BIOMETRIC_AUTH_DENIED;
            this.rootScope.triggerLog(`fetchBiometricStatus Denied: ${JSON.stringify(response)}`, this.fileName, Constants.DEBUG);
            this.stopTimer();
          }  
            else if ( response?.data?.biometricAuthStatus === Constants.biometricConfig.ERROR) {
            this.activeTab.biometricData.step2Complete = false;
            this.activeTab.biometricData.step2Failed = true;
            this.activeTab.biometricData.biometricCompleted = false;
            this.activeTab.biometricData.step2ErrorMsg = Constants.MB_MESSAGES.SOMETHING_WENT_WRONG;
            this.rootScope.triggerLog("fetchBiometricStatus Error: " + JSON.stringify(response), this.fileName);
          }       
          else {
            console.log("fetchBiometricStatus - Unknown Error Status: ", response);
          }
         console.log("fetchBiometricStatus response: ", response);
        }
      },
      error: (error: any) => {
        // On failure, log the error for debugging.
        this.rootScope.triggerLog("fetchBiometricStatus API error: " + JSON.stringify(error), this.fileName);
      }
    });
  }

     /**
    * CCR3-15498 -updateSecurityForPpt() - updates the security for the biometric status after the authentication process.
     * @author Amarjeet
    */
 public updateSecurityForPpt(): void {
    const accountDt = this.rootScope.activeCustTab;

    const model = {
      CS_SECURITY_LEVEL: Constants.securityLevels.SL4,
      authenticateStatus: Constants.FULLY_SECURED,
      iconClass: 'green',
      domain: true
    };

    const body: CallCustomerAttributes = {
      attributes: {
        PINV: 'S',
        CS_AUTH_CD: Constants.AuthCode.MB,
        CS_SECURITY_LEVEL: Constants.securityLevels.SL4,
      }
    };

    accountDt.iconClass = 'green';
    accountDt.genesysAttributes = {
      ...accountDt.genesysAttributes,
      CS_AUTH_CD: Constants.AuthCode.MB
    };

    const participantId =
      accountDt.customerParticipantId && accountDt.customerParticipantId !== 'NA'
        ? accountDt.customerParticipantId
        : this.rootScope.getParticipantId();

    this.genesysCloudService.updateSecurityLevelInGEN(
      accountDt.conversationId,
      participantId,
      body,
      model
    );
  }

}
